<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useTasksStore } from '@/stores/tasks.js';
import { useAuthStore } from '@/stores/auth.js';
import { useToast } from 'primevue/usetoast';

import Card from 'primevue/card';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import Tag from 'primevue/tag';

import TaskRow from './TaskRow.vue';
import TaskDetailsInline from './TaskDetailsInline.vue';

const tasks = useTasksStore();
const auth = useAuthStore();
const toast = useToast();

const tab = ref('mine');
const status = ref('');
const expandedId = ref(null);
const myId = computed(() => auth?.me?.id);

const STATUS_OPTIONS = [
  { label: 'Все', value: '' },
  { label: 'Назначено', value: 'ASSIGNED' },
  { label: 'Готово', value: 'DONE' },
  { label: 'Отклонено', value: 'DECLINED' },
  { label: 'Возвращено', value: 'RETURNED' },
  { label: 'Закрыто', value: 'CLOSED' },
];

// ЕДИНАЯ ФУНКЦИЯ ДЛЯ ЗАГРУЗКИ. ВЫЗЫВАЕТСЯ НАПРЯМУЮ ИЗ ШАБЛОНА
async function loadTasks() {
  tasks.resetList();
  expandedId.value = null;

  const p = {};
  if (tab.value === 'mine' && myId.value) p.assignee = myId.value;
  if (tab.value === 'created' && myId.value) p.creator = myId.value;
  if (status.value) p.status = status.value;


  try {
    await tasks.list(p);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка загрузки', detail: 'Не удалось получить задачи', life: 3500 });
  }
}

// Обработчик переключения табов
function onTabChange(newValue) {
  tab.value = newValue;
  loadTasks();
}

// Логика скролла и раскрытия строк
function onRowToggle(id) {
  expandedId.value = expandedId.value === id ? null : id;
}
const scrollContainer = ref(null);
let isFetchingMore = false;
async function tryLoadMore() {
  if (isFetchingMore || tasks.loading || !tasks.page.next) return;
  isFetchingMore = true;
  try { await tasks.listNext(); } finally { isFetchingMore = false; }
}
function onScroll() {
  const el = scrollContainer.value;
  if (!el) return;
  const threshold = 120;
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  if (nearBottom) tryLoadMore();
}

// Жизненный цикл
onMounted(() => {
  loadTasks();
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', onScroll, { passive: true });
  }
});

onBeforeUnmount(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', onScroll);
  }
});
</script>

<template>
  <Card class="h-full max-h-full">
    <template #content>
      <Tabs v-model:value="tab" class="mb-3 compact-tabs">
        <TabList>
          <Tab value="mine" @click="loadTasks">
            <i class="pi pi-user mr-1 text-xs"></i>
            <span class="text-xs">Мои</span>
          </Tab>
          <Tab value="created" @click="loadTasks">
            <i class="pi pi-share-alt mr-1 text-xs"></i>
            <span class="text-xs">Назначил</span>
          </Tab>
          <Tab value="all" @click="loadTasks">
            <i class="pi pi-list mr-1 text-xs"></i>
            <span class="text-xs">Все</span>
          </Tab>
        </TabList>
      </Tabs>

      <!-- Теги статуса с таким же по структуре обработчиком -->
      <div class="flex flex-wrap items-center gap-2 mb-3">
        <Tag v-for="opt in STATUS_OPTIONS" :key="opt.value || 'all'"
             :value="opt.label"
             :severity="status === opt.value ? 'info' : 'secondary'"
             class="cursor-pointer status-tag"
             @click="status = opt.value; loadTasks()" />
      </div>

      <!-- Список задач -->
      <div ref="scrollContainer" class="flex-1 min-h-0 overflow-auto" style="height: calc(100vh - 280px)">
        <div v-if="tasks.loading && !tasks.items.length" class="p-2 text-center text-sm text-gray-500">Загрузка…</div>

        <div v-for="task in tasks.items" :key="task.id" class="mb-2 relative px-1">
          <TaskRow :task="task"
                   :expanded="expandedId === task.id"
                   @toggle="() => onRowToggle(task.id)" />
          <div v-if="expandedId === task.id" class="ml-3 mt-2">
            <TaskDetailsInline :task-id="task.id" />
          </div>
        </div>

        <div class="py-2 text-center text-sm text-gray-500">
          <span v-if="tasks.loading && tasks.items.length">Загрузка…</span>
          <span v-else-if="!tasks.page.next && (tasks.items?.length || 0) > 0">Это все задачи</span>
          <span v-else-if="!tasks.page.next && (tasks.items?.length || 0) === 0">Нет задач для выбранного фильтра</span>
        </div>
      </div>
    </template>
  </Card>
</template>

<style scoped>
.cursor-pointer { cursor: pointer; }

/* Компактные табы: уменьшение на 20% */
.compact-tabs :deep(.p-tab) {
  padding: 0.4rem 0.8rem; /* было ~0.5rem 1rem */
  font-size: 0.8rem; /* 80% от базового */
  line-height: 1.2;
}

.compact-tabs :deep(.p-tablist) {
  gap: 0.25rem; /* уменьшенный отступ между табами */
}

/* Теги статуса: текст normal, не bold */
.status-tag :deep(.p-tag-label) {
  font-weight: normal;
  font-size: 0.875rem;
}
</style>